'use client'
import { memo, useMemo, type FC } from 'react'
import styled from 'styled-components'
import { SliderFlickity } from '../base'
import { defaultGrid, fullWidth, mq, remainingGridWidth, vw } from '@/styles'
import { FlickityOptions } from 'react-flickity-component'
import { VariantCard } from './product/VariantCard'
import type { TypeCmsVariant } from '@/schema'

type Props = {
  data: TypeCmsVariant[]
  sliderOptions: FlickityOptions
  className?: string
  seeMoreButton?: boolean
}

const getSlides = (data: TypeCmsVariant[], addSpacer = false) => {
  const slides = data.map((item, idx) => {
    if (!item?.id) return null
    return (
      <div key={idx} className='cell'>
        <VariantCard data={item}/>
      </div>
    )
  })

  if (addSpacer) {
    slides.unshift(
      <div key='spacer' className='cell cell--spacer' />
    )
  }

  return slides
}

export const ProductSlider:FC<Props> = memo(({ data, className, sliderOptions, seeMoreButton }) => {
  const Slides = useMemo(() => {
    if (!data || !Array.isArray(data)) return []
    return getSlides(data, !seeMoreButton) // 👈 addSpacer = true if no seeMoreButton
  }, [data, seeMoreButton])

  return (
    <Div className={className}>
      {Slides.length > 0 &&
        <SliderFlickity options={sliderOptions} dynamicSlider={true} reloadOnUpdate={true}>
          {Slides}
          <div key={0 + 'spacer'} className='cell spacer' />
          <div key={1 + 'spacer'} className='cell spacer' />
        </SliderFlickity>
      }
    </Div>
  )
})

export const cardWidth = {
  initial: defaultGrid.mobile.columns.width * 6 + defaultGrid.mobile.gutter.width * 5,
  mobile: defaultGrid.mobile.columns.width * 3 + defaultGrid.mobile.gutter.width * 2,
  phablet: defaultGrid.mobile.columns.width * 2 + defaultGrid.mobile.gutter.width * 1,
  tablet: defaultGrid.tablet.columns.width * 3 + defaultGrid.tablet.gutter.width * 2,
  desktop: defaultGrid.desktop.columns.width * 9/4 + defaultGrid.desktop.gutter.width * 8/3,
}

const Div = styled.div`
  grid-column: 1 / span 6;
  height: 100%;
  max-height: 353px;
  min-height: 300px;
  position: relative;

  ${mq.greaterThan('tablet')}{
    grid-column: 1/  span 12;
  }

  .slider-wrapper {
    ${fullWidth({})}
    /* padding-left: 10px; */
    /* width: 100vw !important; */
  }

  .flickity-viewport {
    max-height: 353px;
    padding-left: ${vw(remainingGridWidth(defaultGrid)?.mobile / 2, 'mobile')};

    ${mq.greaterThan('tablet')} {
      padding-left: ${vw(remainingGridWidth(defaultGrid)?.desktop / 2, 'tablet')};
    }

    ${mq.greaterThan('desktop')} {
      padding-left: 4px;
    }
  }

  .cell {
    margin-right: 10px;
    max-height: 353px;
    max-width: 250px;
    min-height: 300px;
    min-width: 190px;
    position: absolute;
    width: ${vw(cardWidth.initial, 'mobile')};
    z-index: 1;

    ${mq.greaterThan('mobile-xs')} {
      width: ${vw(cardWidth.mobile, 'mobile')};
    }

    ${mq.greaterThan('nexus7')} {
      width: ${vw(cardWidth.phablet, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      width: ${vw(cardWidth.tablet, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      width: ${vw(cardWidth.desktop, 'desktop')};
    }

    &.cell-spacer {
      background: transparent;
      max-width: 450px;
      pointer-events: none;
      width: ${vw(cardWidth.initial * .6, 'mobile')};

      ${mq.greaterThan('mobile-xs')} {
        width: ${vw(cardWidth.mobile * 1.6, 'mobile')};
      }

      ${mq.greaterThan('nexus7')} {
        width: ${vw(cardWidth.phablet * 2, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        width: ${vw(cardWidth.tablet * 3, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        width: ${vw(cardWidth.desktop * 3, 'desktop')};
      }
    }
  }
`
